import moment from 'moment';
import { LAST_30_DAYS, LAST_3_MONTHS, LAST_7_DAYS, LAST_MONTH, LAST_YEAR_1M, PT2H, TODAY } from './AnalyticsConstants';
import { ChartData, TApiResponse, TChartDataItem, TColumnData, TCombinedChartData, TGraphData } from "./analytics";

const generateCombinedData = (chartData: ChartData[]): TCombinedChartData[] => {
  return chartData.map((item) => {
    const columnData = item.data.map((point) => ({ x: point.x, y: point.y, }));
    const lineData = item.data.map((point) => ({ x: point.x, y: point.y, }));
    return { name: item.name, columnData: columnData, lineData: lineData, };
  });
}

export function formatDateToLexicographicalOrder(date: any, timezone: string) {
  const adjustedDate = moment.tz(date, timezone);

  const weekday = adjustedDate.format('ddd');
  const month = adjustedDate.format('MMM');
  const day = adjustedDate.format('D');

  return `${weekday}, ${month} ${day}`;
}

export function formatCustomDate(date: any) {
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dateInOriginalTimezone = moment.parseZone(date, 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ');
  const day = daysOfWeek[dateInOriginalTimezone.day()];
  const hours = dateInOriginalTimezone.format('H');

  return `${day}, ${hours}H`;
}

export const convertApiResponseToGraph = (apiResponse: TApiResponse, granularity: string): TGraphData[] => {
  const graphData: TGraphData[] = [];
  for (const metricName in apiResponse) {
    const metric_name = metricName;
    const data = apiResponse[metric_name];
    const chartData: ChartData[] = [];
    const applicationNames: string[] = [];
    const allDates = new Set<string>();
    for (const dataset of data as any) {
      let value = 0;
      value = parseFloat(dataset?.metric_value === 'Infinity' ? '0' : dataset.metric_value === undefined ? '0' : dataset?.metric_value?.toString() ?? '0');
      const appName = dataset.application_name || 'ARS';
      let dataIndex = applicationNames.indexOf(appName);
      if (dataIndex === -1) {
        applicationNames.push(appName);
        chartData.push({ name: appName, data: [] });
        dataIndex = chartData.length - 1;
      }
      if (!isNaN(value) && isFinite(value)) {
        const dateString = dataset.time_interval;
        allDates.add(dateString);
        chartData[dataIndex].data.push({ x: dateString, y: parseFloat(value.toFixed(3)) });
      }
    }
    const combinedData = generateCombinedData(chartData);
    let dashedArray: number[] = [];
    const chartDataLength = chartData.length;
    if (chartDataLength !== 0) {
      const result = chartDataLength / 2;
      dashedArray = calculateDashArray(true, result, 0);
    }
    const uniqueDates = Array.from(allDates);
    graphData.push({ dashedArray: dashedArray, metric_name: metric_name, chartData: chartData, combinedData: combinedData, xAxis: uniqueDates });
  }
  return graphData;
};

export const combineApiResponseToGraph = (currentApiResponse: TApiResponse, previousApiResponse: TApiResponse, granularity: string): TGraphData[] => {
  const metrics: TGraphData[] = [];

  for (const metricName in currentApiResponse) {
    const currentData = currentApiResponse[metricName];
    const previousData = previousApiResponse[metricName] || [];
    const metric_name = metricName;

    const chartData: ChartData[] = [];
    const allDates = new Set<string>();
    const combinedData = combineDataByApplication(currentData as any, previousData as any, metric_name);

    for (const dataset of combinedData) {
      let value = 0;
      value = parseFloat(dataset?.metric_value === 'Infinity' ? '0' : dataset?.metric_value === undefined ? '0' : dataset?.metric_value?.toString() ?? '0');
      const appName = dataset.application_name || 'ARS';

      if (!isNaN(value) && isFinite(value)) {
        const date = new Date(dataset.Time_Interval || dataset.time_interval);
        allDates.add(dataset.Time_Interval || dataset.time_interval);
        let chartEntry = chartData.find(entry => entry.name === appName);
        if (!chartEntry) {
          chartEntry = { name: appName, data: [] };
          chartData.push(chartEntry);
        }
        chartEntry.data.push({ x: date ?? new Date(), y: parseFloat(value.toFixed(3)) });
      }
    }
    let dashedArray: number[] = [];
    const chartDataLength = chartData.length;
    if (chartDataLength !== 0) {
      const result = chartDataLength / 2;
      dashedArray = calculateDashArray(true, result, result);
    } else {
      console.error("Division by zero error!");
    }

    const uniqueDates = Array.from(allDates);

    metrics.push({
      dashedArray: dashedArray,
      metric_name: metric_name,
      chartData: chartData,
      combinedData: generateCombinedData(chartData),
      xAxis: uniqueDates,
    });
  }

  return metrics;
}

const combineDataByApplication = (currentData: any[], previousData: any[], metricName: string): any[] => {
  const combinedData: any[] = [];
  for (const dataset of currentData) {
    combinedData.push(dataset);
  }
  for (const dataset of previousData) {
    const appName = dataset.application_name || 'ARS';
    dataset.application_name = `${appName} (PP)`;
    combinedData.push(dataset);
  }
  return combinedData;
}

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function calculatePreviousPeriod(dateRange: string[], granularity: string, rangeLabel: string, timeZone: string): [string, string] {
  const startDate = new Date(dateRange[0]);
  const endDate = new Date(dateRange[rangeLabel === TODAY ? 0 : 1]);
  const duration = endDate.getTime() - startDate.getTime();
  const previousStartDate = new Date(startDate.getTime() - duration);
  const previousEndDate = new Date(endDate.getTime() - duration);

  if (granularity !== PT2H) {
    if (rangeLabel !== LAST_7_DAYS) {
      previousStartDate.setDate(previousStartDate.getDate() - 1);
      previousEndDate.setDate(previousEndDate.getDate() - 1);
    }
  }

  if (rangeLabel === TODAY) {
    previousStartDate.setDate(previousStartDate.getDate() - 1);
  }

  if (rangeLabel === LAST_YEAR_1M) {
    previousStartDate.setDate(previousStartDate.getDate() + 1);
  }

  if (rangeLabel === LAST_30_DAYS) {
    previousStartDate.setDate(previousStartDate.getDate() + 1);
    previousEndDate.setDate(previousEndDate.getDate() + 1);
  }

  if (rangeLabel === LAST_3_MONTHS) {
    previousStartDate.setDate(previousStartDate.getDate() + 2)
  }

  if (rangeLabel === LAST_MONTH  || rangeLabel === LAST_3_MONTHS || rangeLabel === LAST_YEAR_1M) {
    previousStartDate.setDate(previousStartDate.getDate() + 2);
    previousEndDate.setDate(previousEndDate.getDate() + 1);
  }
  
  return [formatDate(previousStartDate), formatDate(previousEndDate)];
}

export const calculateDashArray = (isPreviousData: boolean, currentDataSize: number, previousDataSize: number): number[] => {
  const dashArray: number[] = [];
  for (let i = 0; i < currentDataSize; i++) {
    dashArray.push(0);
  }
  if (isPreviousData) {
    for (let i = 0; i < previousDataSize; i++) {
      dashArray.push(4);
    }
  }
  return dashArray;
}

export const formatCurrency = (number: number, locale: string = 'en-US', currencyCode: string = 'USD'): string => {
  if (isNaN(number) || !isFinite(number)) {
    return 'Invalid Number';
  }
  return number.toLocaleString(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });
}

export const updateYAxisTitle = (metricName: string): string => {
  if (["add_to_cart_rate", "cart_conv_rate", "order_conversion_rate"].includes(metricName)) {
    return "The values are in %";
  }
  if (metricName === "order_value") {
    return "The values are in $";
  }
  return "";
};

export const transformText = (text: string): string => {
  const words = text.split('_');

  const transformedText = words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return transformedText;
}

export const findLargestDataArray = (chartData: TChartDataItem[]): TChartDataItem | null => {
  if (chartData.length === 0) {
    return null;
  }

  let largestItem: TChartDataItem = chartData[0];

  for (const item of chartData) {
    if (item.data.length > largestItem.data.length) {
      largestItem = item;
    }
  }

  return largestItem;
}

export const getDateDifferenceLabel = (date1: string, date2: string): string => {
  const date1Obj = new Date(date1);
  const date2Obj = new Date(date2);

  const timeDifference = date2Obj.getTime() - date1Obj.getTime();
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

  if (daysDifference <= 7) {
    return "Week";
  } else if (daysDifference <= 15) {
    return "Weeks";
  } else {
    return "More than 15 Weeks";
  }
}

export function findLargestColumnAndLineArray(data: TColumnData['data']): { x: string; y: number }[] | null {
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }
  let largestArray: { x: string; y: number }[] = data[0].columnData;
  let largestSize: number = data[0].columnData.length;

  for (const obj of data) {
    if (Array.isArray(obj.columnData) && obj.columnData.length > largestSize) {
      largestArray = obj.columnData;
      largestSize = obj.columnData.length;
    }

    if (Array.isArray(obj.lineData) && obj.lineData.length > largestSize) {
      largestArray = obj.lineData;
      largestSize = obj.lineData.length;
    }
  }

  return largestArray;
}

export const areAllChartDataAndCombinedDataEmpty = (data: any[]): boolean => {
  if (data.length === 0) return true;
  return data.every((item) => item.chartData.length === 0 && item.combinedData.length === 0);
}

export const isMetricDataEmpty = (data: any): boolean => {
  if (!data) return true;
  if (data.length === 0) return true;
  return data?.data?.length === 0;
}

export const removeDuplicatesFromArray = (dates: Set<Date>): string[] => {
  const uniqueDateArray = Array.from(dates)
    .sort((a, b) => a.getTime() - b.getTime())
    .map((date) => date.toISOString());
  return uniqueDateArray;
};

export const getDateWithMidnight = (timezone: string, date: Date = new Date()): string => {
  const momentDate = moment(date).tz(timezone);
  const year = momentDate.year();
  const month = String(momentDate.month() + 1).padStart(2, '0');
  const day = String(momentDate.date()).padStart(2, '0');
  return `${year}-${month}-${day} 00:00:00`;
}

export const getFormattedDateTime = (timezone: string, date: Date = new Date()): string => {
  const momentDate = moment(date).tz(timezone);
  const year = momentDate.year();
  const month = String(momentDate.month() + 1).padStart(2, '0');
  const day = String(momentDate.date()).padStart(2, '0');
  const hours = String(momentDate.hours()).padStart(2, '0');
  const minutes = String(momentDate.minutes()).padStart(2, '0');
  const seconds = String(momentDate.seconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export const adjustDateRangeWithCurrentTime = (dateRange: string[], timezone: string): [string, string] => {
  const startDate = dateRange[0];
  const currentMoment = moment().tz(timezone);

  const formattedCurrentTime = currentMoment.format('YYYY-MM-DD HH:mm:ss');

  return [startDate, formattedCurrentTime];
};

export const getStartAndEndOfPreviousYear = (timezone: string) => {
  const today = moment().tz(timezone);
  const startOfYear = today.clone().subtract(1, 'year').startOf('year').toDate();
  const endOfYear = today.clone().subtract(1, 'year').endOf('year').toDate();

  return { startOfYear, endOfYear };
}

export const getMonthAbbreviation = (fullMonthName: string) => {
  const monthNumber = moment().month(fullMonthName).month();
  return moment().month(monthNumber).format('MMM');
};

export const formatMetricValue = (metricName: string, value: number): string | number => {
  const formatters: Record<string, (v: number) => string | number> = {
    'Add To Cart Rate': v => v.toFixed(3) + '%',
    'Interactions Conversion Rate': v => v.toFixed(3) + '%',
    'Click Through Rate': v => v.toFixed(3) + '%',
    'Order Conversion Rate': v => v.toFixed(3) + '%',
    'Order Value': v => formatCurrency(parseFloat(v.toFixed(2))),
    'Average Order Value': v => formatCurrency(parseFloat(v.toFixed(2))),
    'Revenue Generated': v => formatCurrency(parseFloat(v.toFixed(2))),
    'Average Line Items Per Order': v => v.toFixed(2)
  };
  return (formatters[metricName] || (v => v))(value);
};
